import React from 'react';
import brandConfig from '../../config/brandConfig';
import { Phone, MapPin, Facebook, Youtube, ArrowRight, PlayCircle } from 'lucide-react';

const Footer = () => {
  const whatsappNumber = brandConfig.whatsappSupport;
  const whatsappDisplay = whatsappNumber.startsWith('+') ? whatsappNumber : `+${whatsappNumber}`;
  const whatsappLink = whatsappNumber.startsWith('+')
    ? `https://wa.me/${whatsappNumber.replace('+', '')}`
    : `https://wa.me/${whatsappNumber}`;

  return (
    <footer className="relative overflow-hidden grain-overlay" style={{
      background: 'linear-gradient(135deg, #0a1929 0%, #102a43 50%, #0a1929 100%)'
    }}>
      {/* Decorative */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-11 h-11 rounded-xl overflow-hidden shadow-lg flex-shrink-0">
                  <img src="/images/android-chrome-192x192.png" alt={brandConfig.getLogoAlt()} width={44} height={44} className="rounded-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-white tracking-tight">
                    <span style={{ color: brandConfig.primaryColor }}>{brandConfig.namePrefix}</span>{brandConfig.nameSuffix}
                  </h3>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/30">Plataforma Inmobiliaria</p>
                </div>
              </div>
              <p className="text-white/40 text-sm mb-6 leading-relaxed">
                La plataforma inmobiliaria lider en Latinoamerica. Conectamos inmobiliarias certificadas
                con compradores para transacciones seguras y exitosas.
              </p>
              <div className="flex space-x-3">
                <a href="https://www.facebook.com/lotespro" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center transition-colors text-white/50 hover:text-white">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="https://www.youtube.com/@lotespro-com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center transition-colors text-white/50 hover:text-white">
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-white/40 mb-6">Enlaces Rapidos</h4>
              <ul className="space-y-3">
                {[
                  { href: '/', text: 'Inicio' },
                  { href: '#about', text: 'Sobre Nosotros' },
                  { href: '#how-it-works', text: 'Como Funciona' },
                  { href: '#contact', text: 'Contacto' }
                ].map((link, i) => (
                  <li key={i}>
                    <a href={link.href} className="text-white/50 hover:text-white text-sm font-medium transition-colors flex items-center gap-2 group">
                      <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Countries */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-white/40 mb-6">Paises</h4>
              <ul className="space-y-3">
                {[
                  { href: 'https://peru.lotespro.com', flag: '\ud83c\uddf5\ud83c\uddea', name: 'Peru' },
                  { href: 'https://mexico.lotespro.com', flag: '\ud83c\uddf2\ud83c\uddfd', name: 'Mexico' },
                  { href: 'https://colombia.lotespro.com', flag: '\ud83c\udde8\ud83c\uddf4', name: 'Colombia' },
                  { href: 'https://chile.lotespro.com', flag: '\ud83c\udde8\ud83c\uddf1', name: 'Chile' },
                  { href: 'https://argentina.lotespro.com', flag: '\ud83c\udde6\ud83c\uddf7', name: 'Argentina' }
                ].map((country, i) => (
                  <li key={i}>
                    <a href={country.href} className="text-white/50 hover:text-white text-sm font-medium transition-colors flex items-center gap-2 group">
                      <span className="text-base">{country.flag}</span>
                      {country.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Videos */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-white/40 mb-6">Como Funciona</h4>
              <ul className="space-y-3">
                {['Ver Tutoriales', 'Funcionalidades', 'Guias de Uso', 'Canal de YouTube'].map((text, i) => (
                  <li key={i}>
                    <a href="https://www.youtube.com/@lotespro-com" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm font-medium transition-colors flex items-center gap-2 group">
                      <PlayCircle className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100" />
                      {text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-white/40 mb-6">Contacto</h4>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4 text-white/50" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/30">WhatsApp</p>
                    <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-white font-semibold text-sm hover:text-orange-400 transition-colors">
                      {whatsappDisplay}
                    </a>
                    <p className="text-white/30 text-xs">Lun - Vie: 8:00 - 18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-white/50" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/30">Oficina Principal</p>
                    <p className="text-white font-semibold text-sm">Lima, Peru</p>
                    <p className="text-white/30 text-xs">GMT-5</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="border-t border-white/5 py-12">
          <div className="glass rounded-3xl p-8">
            <div className="text-center">
              <h3 className="text-xl font-extrabold text-white mb-3 tracking-tight">Listo para encontrar tu lote ideal?</h3>
              <p className="text-white/40 text-sm mb-6 max-w-2xl mx-auto">
                Explora miles de propiedades en toda Latinoamerica y encuentra el lote perfecto
              </p>
              <a
                href="#"
                className="inline-flex items-center gap-2 text-white px-7 py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/30 hover:scale-[1.02]"
                style={{ background: brandConfig.getPrimaryGradient() }}
                onClick={(e) => { e.preventDefault(); document.querySelector('#hero')?.scrollIntoView({ behavior: 'smooth' }); }}
              >
                Comenzar Ahora
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/5 py-8">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="text-white/20 text-xs font-medium">
              &copy; 2024 {brandConfig.domain}. Todos los derechos reservados.
            </div>
            <div>
              <p className="text-white/30 text-xs">
                Creado por{' '}
                <a href="https://cloud4geo.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors font-semibold">
                  Cloud4Geo
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
